import React, { FC } from 'react';
import { Typography } from '../Typography';
import styles from './Table.module.css';
import { useThemes } from '@hooks/useThemes';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type TableSubComponents = {
    Options: typeof Options;
};

export interface TableProps {
    id?: string;
    classes?: string;
    headerFontSize?: '12' | '14';
    headerCols?: {
        classes?: string;
        fit?: boolean;
        hideOnMobile?: boolean;
        hideOnDesktop?: boolean;
        text: string | JSX.Element;
    }[];
    hideHeaderOnMobile?: boolean;
    contentRows: { classes?: string; colspan?: number; content: string | JSX.Element | JSX.Element[] }[][];
    borderless?: boolean;
    compressedSize?: boolean;
    onRowReorder?: (newRows: TableProps['contentRows']) => void;
    draggable?: boolean;
    rowIds?: string[];
}

export const Table: FC<TableProps> & TableSubComponents = ({
    id,
    classes,
    headerFontSize,
    headerCols,
    hideHeaderOnMobile,
    contentRows,
    borderless,
    compressedSize,
    onRowReorder,
    draggable = false,
    rowIds = [],
}: TableProps) => {
    const { useGrayscaleTheme } = useThemes();
    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragEnd = (event: DragEndEvent) => {
        if (!draggable || !onRowReorder) return;
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = rowIds.findIndex((id) => id === active.id);
        const newIndex = rowIds.findIndex((id) => id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        const newRows = arrayMove(contentRows, oldIndex, newIndex);
        onRowReorder(newRows);
    };

    return (
        <div className="table-responsive">
            <table
                id={id || undefined}
                data-testid="table"
                className={`table ${styles.table} ${compressedSize ? `table-sm ${styles['table-sm']}` : ''} ${
                    borderless ? 'table-borderless' : ''
                } mb-0 ${classes || ''}`}>
                {headerCols && (
                    <thead>
                        <tr className={hideHeaderOnMobile ? 'd-none d-md-table-row' : ''}>
                            {headerCols.map((item, idx) => (
                                <th
                                    key={idx}
                                    className={`${item.classes || ''} ${item.fit ? styles.fit : ''} ${
                                        item.hideOnMobile ? styles['cell-hide-on-mobile'] : ''
                                    } ${item.hideOnDesktop ? styles['cell-hide-on-desktop'] : ''} ${
                                        useGrayscaleTheme ? styles['theme-grayscale'] : ''
                                    }`}>
                                    <Typography fontSize={headerFontSize || '14'}>{item.text}</Typography>
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}
                {draggable ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                            <tbody>
                                {contentRows.map((row, idx) => (
                                    <SortableRow key={rowIds[idx]} id={rowIds[idx]} row={row} headerCols={headerCols} />
                                ))}
                            </tbody>
                        </SortableContext>
                    </DndContext>
                ) : (
                    <tbody>
                        {contentRows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => {
                                    const hideOnMobile = headerCols && headerCols[cellIndex]?.hideOnMobile;
                                    const hideOnDesktop = headerCols && headerCols[cellIndex]?.hideOnDesktop;
                                    const isFit = headerCols && headerCols[cellIndex]?.fit;
                                    return (
                                        <td
                                            key={cellIndex}
                                            className={`${cell.classes || ''} ${isFit ? styles.fit : ''} ${
                                                hideOnMobile ? styles['cell-hide-on-mobile'] : ''
                                            } ${hideOnDesktop ? styles['cell-hide-on-desktop'] : ''}`}>
                                            {cell.content}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                )}
            </table>
        </div>
    );
};

interface SortableRowProps {
    id: string;
    row: TableProps['contentRows'][number];
    headerCols?: TableProps['headerCols'];
}

const SortableRow: FC<SortableRowProps> = ({ id, row, headerCols }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: 'grab',
    };

    return (
        <tr ref={setNodeRef} style={style}>
            {row.map((cell, cellIndex) => {
                const hideOnMobile = headerCols && headerCols[cellIndex]?.hideOnMobile;
                const hideOnDesktop = headerCols && headerCols[cellIndex]?.hideOnDesktop;
                const isFit = headerCols && headerCols[cellIndex]?.fit;

                return (
                    <td
                        key={cellIndex}
                        className={`${cell.classes || ''} ${isFit ? styles.fit : ''} ${
                            hideOnMobile ? styles['cell-hide-on-mobile'] : ''
                        } ${hideOnDesktop ? styles['cell-hide-on-desktop'] : ''}`}
                        {...(cellIndex === 0 ? listeners : {})}
                        {...(cellIndex === 0 ? attributes : {})} // draggable only on first cell
                    >
                        {cell.content}
                    </td>
                );
            })}
        </tr>
    );
};

interface OptionsProps {
    classes?: string;
    options: JSX.Element[];
}

const Options: FC<OptionsProps> = ({ classes, options }) => (
    <div className={`${styles['table-options']} ${classes || ''}`}>
        {options.map((option, idx) => (
            <div key={idx} className={styles.option}>
                {option}
            </div>
        ))}
    </div>
);

Table.Options = Options;
